import { Mail, Phone, MapPin, ExternalLink } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { LocalizedLink } from "@/components/LocalizedLink";

const partners = [
  { name: "Legalizačné centrum", href: "https://www.legalizacnecentrum.sk", description: "Apostille & Superlegalizácia" },
  { name: "AKMV advokátska kancelária", href: "https://www.akmv.sk", description: "Právne služby" },
];

export function MainFooter() {
  const { t } = useLanguage();

  const services = [
    { nameKey: "footer.service_translations", href: "/sluzby/uradne-preklady" },
    { nameKey: "footer.service_apostila", href: "/sluzby/apostila" },
    { nameKey: "footer.service_superlegalization", href: "/sluzby/superlegalizacia" },
    { nameKey: "footer.service_foreign_documents", href: "/sluzby/zahranicne-dokumenty" },
    { nameKey: "footer.service_calculator", href: "/kalkulator" },
  ];

  const company = [
    { nameKey: "footer.company_about", href: "/o-nas" },
    { nameKey: "footer.company_pricing", href: "/cennik" },
    { nameKey: "footer.company_faq", href: "/faq" },
    { nameKey: "footer.company_contact", href: "/kontakt" },
  ];

  return (
    <footer className="bg-primary text-primary-foreground">
      {/* Main Footer */}
      <div className="container mx-auto px-4 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12">
          {/* Brand */}
          <div className="lg:col-span-2 space-y-6">
            <LocalizedLink to="/" className="flex flex-col">
              <span className="font-display font-bold text-2xl leading-tight">
                Prelozim<span className="text-secondary">.sk</span>
              </span>
              <span className="text-[10px] font-semibold uppercase tracking-widest text-primary-foreground/60">
                {t("footer.tagline")}
              </span>
            </LocalizedLink>
            <p className="text-primary-foreground/80 leading-relaxed">
              {t("footer.description")}
            </p>
            <div className="space-y-2">
              <p className="text-sm font-semibold text-secondary">{t("footer.partner_sites")}</p>
              {partners.map((partner) => (
                <a
                  key={partner.href}
                  href={partner.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-primary-foreground/80 hover:text-secondary transition-colors text-sm"
                >
                  <ExternalLink className="w-3 h-3" />
                  {partner.name}
                </a>
              ))}
            </div>
          </div>

          {/* Services */}
          <div>
            <h3 className="font-display font-bold text-lg mb-6">{t("footer.services_title")}</h3>
            <ul className="space-y-4">
              {services.map((service) => (
                <li key={service.href}>
                  <LocalizedLink
                    to={service.href}
                    className="text-primary-foreground/80 hover:text-secondary transition-colors"
                  >
                    {t(service.nameKey)}
                  </LocalizedLink>
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div>
            <h3 className="font-display font-bold text-lg mb-6">{t("footer.company_title")}</h3>
            <ul className="space-y-4">
              {company.map((item) => (
                <li key={item.href}>
                  <LocalizedLink
                    to={item.href}
                    className="text-primary-foreground/80 hover:text-secondary transition-colors"
                  >
                    {t(item.nameKey)}
                  </LocalizedLink>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="font-display font-bold text-lg mb-6">{t("footer.contact_title")}</h3>
            <ul className="space-y-4">
              <li className="flex items-center gap-3 text-primary-foreground/80">
                <Mail className="w-5 h-5 text-secondary flex-shrink-0" />
                <span>office@legalizacnecentrum.sk</span>
              </li>
              <li className="flex items-center gap-3 text-primary-foreground/80">
                <Phone className="w-5 h-5 text-secondary" />
                0907 999 909
              </li>
              <li className="flex items-center gap-3 text-primary-foreground/80">
                <Phone className="w-5 h-5 text-secondary" />
                02/ 4333 3509
              </li>
              <li className="flex items-start gap-3 text-primary-foreground/80">
                <MapPin className="w-5 h-5 text-secondary flex-shrink-0 mt-0.5" />
                <span>Legalizačné centrum Bratislava<br />Pluhová 17, 831 03 Bratislava</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-primary-foreground/10">
        <div className="container mx-auto px-4 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-primary-foreground/60">
            <p>© {new Date().getFullYear()} {t("footer.copyright")}</p>
            <div className="flex gap-6">
              <LocalizedLink to="/obchodne-podmienky" className="hover:text-primary-foreground transition-colors">{t("footer.terms")}</LocalizedLink>
              <LocalizedLink to="/ochrana-sukromia" className="hover:text-primary-foreground transition-colors">{t("footer.privacy")}</LocalizedLink>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
